import { resourceService } from "@libs/axios";
import {
  fetchMeetingsByTimeRange,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  fetchSchools,
  fetchClasses,
  fetchClassesBySchool,
  fetchTeachers,
  fetchSchoolsPaginated,
  fetchClassesBySchoolPaginated,
} from "@store/actions/calendarActions";
import {
  setLoading,
  setMeetingList,
  setSchools,
  setClasses,
  setClassesBySchool,
  setTeachers,
  setLoadingAction,
  setSchoolsPaginated,
  setClassesBySchoolPaginated,
} from "@store/reducers/calendarReducer";
import { toast } from "react-toastify";
import { call, put, takeLatest, select } from "redux-saga/effects";
import { ResponseType } from "@store/index";
import dayjs from 'dayjs';
import { toGMT7HourMinute } from "@common/helpers";

const API_URL = process.env.REACT_APP_API_URL;

function* fetchMeetingsByTimeRangeSaga(action: any): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const { filters } = action.payload;
    // Get current user role from Redux store
    const auth = yield select((state) => state.auth);
    const isStudent = auth?.role === 'student';
    const isTeacher = auth?.role === 'admin_class';
    const start_time_min = dayjs(filters.start_time_min).subtract(7, 'day').toISOString();
    const start_time_max = dayjs(filters.start_time_max).add(7, 'day').toISOString();
    if (!filters.class_id) {
      const sessionMeetingClass = sessionStorage.getItem('meetingClasses');
      if (sessionMeetingClass !== 'null' && sessionMeetingClass !== 'undefined' && sessionMeetingClass !== null && sessionMeetingClass !== undefined) {
        filters.class_id = Number(sessionMeetingClass);
      } else if (!isTeacher) {
        yield put(setMeetingList([]));
        yield put(setLoading(false));
        return;
      }
    }
    // Use different API endpoint based on user role
    const endpoint = isStudent ? '/api/student_schedule' : '/api/zoom_meetings';

    let page = 1;
    const page_size = 100;
    let allResults: any[] = [];
    let hasNext = true;
    let firstResponse: ResponseType | null = null;

    while (hasNext) {
      const params: Record<string, any> = {
        start_time_min,
        start_time_max,
        page,
        page_size,
      };
      if (filters.class_id) {
        params.class_id = filters.class_id;
      }
      const response: ResponseType = yield call(resourceService.get,
        API_URL + endpoint, { params });

      if (response.status === 200) {

        if (page === 1) firstResponse = response;

        if (response.data?.results) {
          allResults = allResults.concat(response.data.results);
        } else if (Array.isArray(response.data)) {

          allResults = allResults.concat(response.data);
        }

        if (response.data?.next) {
          page++;
        } else {
          hasNext = false;
        }
      } else {
        hasNext = false;
      }
    }


    if (firstResponse && firstResponse.data) {
      if (firstResponse.data.results) {
        firstResponse.data.results = allResults;
      } else if (Array.isArray(firstResponse.data)) {
        firstResponse.data = allResults;
      }
      yield put(setMeetingList(firstResponse.data));
    } else {
      yield put(setMeetingList(allResults));
    }
    yield put(setLoading(false));
  } catch (error) {
    yield put(setLoading(false));
  }
}

function* createMeetingSaga(action: any) {
  try {
    yield put(setLoadingAction(1));
    const { filters } = action.payload;
    console.log('Filters:', filters);
    // Validate required fields
    if (!filters.meeting_topic || !filters.start_time || !filters.duration ||
        !filters.school_id || !filters.class_id || !filters.host_id) {
      toast.error("Thiếu thông tin bắt buộc");
      yield put(setLoadingAction(0));
      return;
    }

    const response: ResponseType = yield call(resourceService.post,
      API_URL + '/api/zoom_meetings/',
      {
        meeting_topic: filters?.meeting_topic,
        descriptions: filters?.descriptions,
        start_time: filters?.start_time,
        duration: filters?.duration,
        school: filters?.school_id,
        class_instance: filters?.class_id,
        host: filters?.host_id,
        meeting_password: "abc123",
        notification_before: 15,
        status: "scheduled",
        module:filters.module,
        meeting_color: filters?.meeting_color,
        type: filters?.type,
        submodule:`${filters.submodule.join(',')}`
      }
    );
    
    if (response.status === 201) {
      toast.success("Tạo tiết học thành công");
      console.log('Response từ server:', response.data);
      yield put(setLoadingAction(0));
      if (filters?.onSuccess) {
        yield call(filters?.onSuccess, response);
      }
    }
  }
  catch (error) {
    console.log('Error create meeting:', error);
  
    const response = (error as any)?.response?.data ?? (error as any)?.data ?? error;
  
    const isConflict =
      Array.isArray(response?.details) &&
      response.details.some((d: any) => d?.conflict_start && d?.conflict_end);
  
    if (isConflict) {
      const messages = response.details
        .filter((d: any) => d?.conflict_start && d?.conflict_end)
        .map((d: any) => {
          const start = toGMT7HourMinute(d?.conflict_start);
          const end = toGMT7HourMinute(d?.conflict_end);
          return `Lịch học bị trùng với buổi học khác lúc ${start} - ${end}`;
        });
  
      // gộp nhiều conflict thành 1 toast, mỗi conflict xuống dòng
      toast.error(messages.join('\n'));
    } else {
      toast.error("Có lỗi xảy ra khi tạo tiết học");
    }
  
    yield put(setLoadingAction(0));
  }
}

function* updateMeetingSaga(action: any) {
  try {
    yield put(setLoadingAction(1));
    const { id, data } = action.payload;
    
    // Validate required fields
    if (!data.meeting_topic || !data.start_time || !data.duration ||
        !data.school_id || !data.class_id || !data.host_id) {
      toast.error("Thiếu thông tin bắt buộc");
      yield put(setLoadingAction(0));
      return;
    }
    const response: ResponseType = yield call(
      resourceService.put,
      `${API_URL}/api/zoom_meetings/${id}/`,
      {
        meeting_topic: data.meeting_topic,
        descriptions: data.descriptions,
        duration: data.duration,
        start_time: data?.start_time[0],
        meeting_color: data.meeting_color,
        school: data.school_id,
        class_instance: data.class_id,
        host: data.host_id,
        meeting_password: "abc123",
        notification_before: 15,
        status: data?.status,
        module:data.module || null,
        submodule: data.submodule ? data.submodule.join(',') : null,
        type: data?.type,
      }
    );

    if (response.status === 200) {
      toast.success("Cập nhật lịch học thành công");
      if (data.onSuccess) {
        yield call(data.onSuccess);
      }
      yield put(setLoadingAction(0));
    }
  } catch (error) {
    toast.error("Có lỗi xảy ra khi cập nhật lịch học");
    yield put(setLoadingAction(0));
  }
}

function* deleteMeetingSaga(action: any) {
  try {
    yield put(setLoading(true));
    const { id, start_time, class_id } = action.payload;
    // Fri Jun 27 2025 15:30:00 GMT+0700 (Indochina Time)

    const start = new Date(start_time);
    const start_time_min = new Date(start);
          start_time_min.setDate(start.getDate() - 40);
    const start_time_max = new Date(start);
          start_time_max.setDate(start.getDate() + 40);
    const response: ResponseType = yield call(
      resourceService.delete,
      `${API_URL}/api/zoom_meetings/${id}/`
    );

    if (response.status === 204) {
      toast.success("Xóa lịch học thành công");
      const response: ResponseType = yield call(resourceService.get,
        API_URL + '/api/zoom_meetings', {
        params: {
          start_time_min: start_time_min.toISOString(),
          start_time_max: start_time_max.toISOString(),
          page: 1,
          page_size: 100,
          class_id: class_id || null,
        }
      });
      if (response.status === 200) {
      yield put(setMeetingList(response.data))
      yield put(setLoading(false))
    }
    }
  } catch (error) {
    toast.error("Có lỗi xảy ra khi xóa lịch học");
    yield put(setLoading(false));
  }
}

function getCurrentMonthRange() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth(); // 0-based index

  const start = new Date(Date.UTC(year, month, 1, 0, 0, 0));
  const end = new Date(Date.UTC(year, month + 1, 0, 0, 0, 0)); // Last day of the month

  return {
    start_time_min: start.toISOString(),
    start_time_max: end.toISOString(),
  };
}

function* fetchSchoolsSaga() {
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/schools/`,
      {
        params: {
          page: 1,
          page_size: 500,
          status: 1
        }
      }
    );

    if (response.status === 200) {
      yield put(setSchools(response.data));
      yield put(setLoading(false));
    }
  } catch (error) {
    console.error('Error fetching schools:', error);
    yield put(setLoading(false));
  }
}

function* fetchClassesSaga() {
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/classes/`,
      {
        params: {
          page: 1,
          page_size: 20,
        }
      }
    );

    if (response.status === 200) {
      yield put(setClasses(response.data));
      yield put(setLoading(false));
    }
  } catch (error) {
    console.error('Error fetching classes:', error);
    yield put(setLoading(false));
  }
}

function* fetchClassesBySchoolSaga(action: any) {
  try {
    yield put(setLoading(true));
    const { schoolId } = action.payload;
    if (schoolId) {
      const response: ResponseType = yield call(
        resourceService.get,
        `${API_URL}/api/classes/`,
        {
          params: {
            school: schoolId,
            page: 1,
            page_size: 160,
            status: 1,
            is_current_school_year:true
          }
        }
      );

      if (response.status === 200) {
        yield put(setClassesBySchool(response.data));
        yield put(setLoading(false));
      }
    } else {
      yield put(setClassesBySchool([]));
      yield put(setLoading(false));
    }
  } catch (error) {
    console.error('Error fetching classes by school:', error);
    yield put(setLoading(false));
  }
}

function* fetchTeachersSaga(action: any) {
  try {
    yield put(setLoading(true));
    const { schoolId, classId,type, page, pageSize } = action.payload;
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/get_teacher_schools/`,
      {
        params: {
          school_id: schoolId,
          type:type?type:null,
          class_id: classId,
          page: page?page:1,
          page_size: pageSize?pageSize:100
        }
      }
    );

    if (response.status === 200) {
      yield put(setTeachers(response.data));
      yield put(setLoading(false));
    }
  } catch (error) {
    console.error('Error fetching teachers:', error);
    yield put(setLoading(false));
  }
}

function* fetchSchoolsPaginatedSaga(action: any): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const { page, page_size, search } = action.payload;
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/schools/`,
      {
        params: {
          page,
          page_size,
          status: 1,
          ...(search ? { name: search } : {}),
        },
      }
    );
    if (response.status === 200) {
      yield put(setSchoolsPaginated(response.data));
    }
  } catch (error) {
    console.error('Error fetching schools paginated:', error);
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchClassesBySchoolPaginatedSaga(action: any): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const { schoolId, page, page_size, search } = action.payload;
    const response: ResponseType = yield call(
      resourceService.get,
      `${API_URL}/api/classes/`,
      {
        params: {
          school: schoolId,
          page,
          page_size,
          status: 1,
          is_current_school_year: true,
          ...(search ? { code_name: search } : {}),
        },
      }
    );
    if (response.status === 200) {
      yield put(setClassesBySchoolPaginated(response.data));
    }
  } catch (error) {
    console.error('Error fetching classes by school paginated:', error);
  } finally {
    yield put(setLoading(false));
  }
}

export default function* calendarSaga() {
  yield takeLatest(fetchMeetingsByTimeRange, fetchMeetingsByTimeRangeSaga)
  yield takeLatest(createMeeting, createMeetingSaga)
  yield takeLatest(updateMeeting, updateMeetingSaga)
  yield takeLatest(deleteMeeting, deleteMeetingSaga)
  yield takeLatest(fetchSchools, fetchSchoolsSaga)
  yield takeLatest(fetchClasses, fetchClassesSaga)
  yield takeLatest(fetchClassesBySchool, fetchClassesBySchoolSaga)
  yield takeLatest(fetchTeachers, fetchTeachersSaga)
  yield takeLatest(fetchSchoolsPaginated, fetchSchoolsPaginatedSaga)
  yield takeLatest(fetchClassesBySchoolPaginated, fetchClassesBySchoolPaginatedSaga)
}
